import React, { useState } from 'react';
import { AsyncEditableAttributeModal } from '@atlassian/jira-servicedesk-insight-editable-attribute-modal/src/async.tsx';
import type { AttributeValuesInfo } from '@atlassian/jira-servicedesk-insight-editable-attribute-modal/src/common/types.tsx';
import { getBBRepoValues } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/bitbucket-repo/index.tsx';
import { getDefaultAttributeArrayValue } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { getGroupValues } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/group/index.tsx';
import { getOpsgenieTeamValues } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/opsgenie-team/index.tsx';
import { getProjectValues } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/project/index.tsx';
import { getObjectReferenceOptions } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/referenced-object/index.tsx';
import { getUserValues } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/user/index.tsx';
import type { UserAttributeValue } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-value.tsx';
import type {
	BitbucketRepoAttribute,
	OpsgenieTeamAttribute,
	DefaultAttribute,
	GroupAttribute,
	ProjectAttribute,
	UserAttribute,
	ObjectAttribute,
	StatusAttribute,
} from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute.tsx';
import type { OpsgenieTeam } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import {
	matchDefaultAttribute,
	matchInsightObjectAttribute,
} from '@atlassian/jira-servicedesk-insight-object-types/src/common/utils/type-matchers/index.tsx';
import { MultiValueLozenge } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/multi-value-lozenge/index.tsx';
import type { ReadViewProps } from '../common/types.tsx';
import {
	BitbucketReposReadView,
	type BitbucketRepoReadViewProps,
} from './bitbucket-repo/index.tsx';
import { BooleanReadView } from './boolean/index.tsx';
import { DateTimeReadView } from './date-time/index.tsx';
import { DateReadView } from './date/index.tsx';
import { GroupsReadView } from './group/index.tsx';
import { OpsgenieTeamsReadView } from './opsgenie-team/index.tsx';
import { ProjectsReadView } from './project/index.tsx';
import {
	ReferencedObjectsReadView,
	type ReferencedObjectReadViewType,
} from './referenced-object/index.tsx';
import { SelectReadView } from './select/index.tsx';
import { StatusReadView } from './status/main.tsx';
import type { Status as StatusReadViewType } from './status/status-lozenge/index.tsx';
import { TextAreaReadView } from './text-area/index.tsx';
import { TextReadView } from './text/index.tsx';
import { UsersReadView, type User } from './user/index.tsx';

const renderDefaultAttribute = ({
	defaultAttribute,
	showPlaceholderIfEmpty,
}: {
	defaultAttribute: DefaultAttribute;
	showPlaceholderIfEmpty?: boolean;
}) => {
	const commonProps: {
		showPlaceholderIfEmpty?: boolean;
	} = {
		showPlaceholderIfEmpty,
	};
	const renderText = (value: string | null) => (
		<TextReadView
			{...commonProps}
			value={value}
			defaultAttributeType={defaultAttribute.objectTypeAttribute}
		/>
	);
	const renderMultiText = (values: string[]) => (
		<TextReadView
			{...commonProps}
			value={values}
			defaultAttributeType={defaultAttribute.objectTypeAttribute}
		/>
	);

	return matchDefaultAttribute(defaultAttribute, {
		url: renderMultiText,
		email: renderMultiText,
		float: renderText,
		integer: renderText,
		ipAddress: renderText,
		text: renderText,
		textarea: (value) => <TextAreaReadView {...commonProps} value={value} />,
		select: (value) => <SelectReadView {...commonProps} value={value} />,
		boolean: (value) => <BooleanReadView value={value} />,
		date: (value) => <DateReadView {...commonProps} value={value} />,
		datetime: (value) => <DateTimeReadView {...commonProps} value={value} />,
	});
};

export const ReadView = ({ attribute, showPlaceholderIfEmpty }: ReadViewProps) =>
	matchInsightObjectAttribute(attribute, {
		default: (defaultAttribute) =>
			renderDefaultAttribute({
				defaultAttribute,
				showPlaceholderIfEmpty,
			}),
		object: (objectAttribute: ObjectAttribute) => {
			const referencedObjects = objectAttribute.objectTypeValues.map<ReferencedObjectReadViewType>(
				({ referencedObject }) => {
					const { id, objectKey, label, avatar } = referencedObject;
					return { id, objectKey, label, avatar };
				},
			);
			return (
				<ReferencedObjectsReadView
					referencedObjects={referencedObjects}
					showPlaceholderIfEmpty={showPlaceholderIfEmpty}
				/>
			);
		},
		user: (userAttribute: UserAttribute) => {
			const users: User[] = userAttribute.userTypeValues.map<User>(
				({ user }: UserAttributeValue) => {
					const { displayName, avatarUrl, key } = user;
					return { displayName, avatarUrl, accountId: key };
				},
			);
			return <UsersReadView users={users} showPlaceholderIfEmpty={showPlaceholderIfEmpty} />;
		},
		group: (groupAttribute: GroupAttribute) => {
			const groups = groupAttribute.groupTypeValues.map((groupAttributeValue) => ({
				name: groupAttributeValue.group.name,
			}));
			return <GroupsReadView groups={groups} showPlaceholderIfEmpty={showPlaceholderIfEmpty} />;
		},
		project: (projectAttribute: ProjectAttribute) => {
			const projects = projectAttribute.projectTypeValues.map((projectAttributeValue) => ({
				name: projectAttributeValue.project.name,
				id: projectAttributeValue.project.id,
				avatarUrl: projectAttributeValue.project.avatarUrl,
				key: projectAttributeValue.project.key,
			}));
			return (
				<ProjectsReadView projects={projects} showPlaceholderIfEmpty={showPlaceholderIfEmpty} />
			);
		},
		bitbucketRepo: (bitbucketRepoAttribute: BitbucketRepoAttribute) => {
			const bitbucketRepos: BitbucketRepoReadViewProps[] =
				bitbucketRepoAttribute.bitbucketRepoTypeValues.map((bitbucketRepoTypeValue) => ({
					uuid: bitbucketRepoTypeValue.bitbucketRepo.uuid,
					name: bitbucketRepoTypeValue.bitbucketRepo.name,
					avatarUrl: bitbucketRepoTypeValue.bitbucketRepo.avatarUrl,
					url: bitbucketRepoTypeValue.bitbucketRepo.url,
				}));
			return (
				<BitbucketReposReadView
					bitbucketRepos={bitbucketRepos}
					showPlaceholderIfEmpty={showPlaceholderIfEmpty}
				/>
			);
		},
		status: (statusAttribute: StatusAttribute) => {
			const [status = null] = statusAttribute.statusTypeValues.map<StatusReadViewType>(
				(statusAttributeValue) => {
					const { id, name, category } = statusAttributeValue.status;
					return { id, name, category };
				},
			);
			return <StatusReadView status={status} />;
		},
		opsgenieTeam: (opsgenieTeamAttribute: OpsgenieTeamAttribute) => {
			const opsgenieTeams: OpsgenieTeam[] = opsgenieTeamAttribute.opsgenieTeamTypeValues.map(
				(opsgenieTeamTypeValue) => ({
					id: opsgenieTeamTypeValue.opsgenieTeam.id,
					name: opsgenieTeamTypeValue.opsgenieTeam.name,
					avatarUrl: opsgenieTeamTypeValue.opsgenieTeam.avatarUrl,
					url: opsgenieTeamTypeValue.opsgenieTeam.url,
				}),
			);
			return (
				<OpsgenieTeamsReadView
					opsgenieTeams={opsgenieTeams}
					showPlaceholderIfEmpty={showPlaceholderIfEmpty}
				/>
			);
		},
	});

type LozengeAndAttributeValueModalProps = {
	attributeValuesInfo: AttributeValuesInfo;
	attributeName: string;
};
export const LozengeAndAttributeValueModal = (props: LozengeAndAttributeValueModalProps) => {
	const { attributeValuesInfo, attributeName } = props;
	const [isEditableAttributeModalOpen, setIsEditableAttributeModalOpen] = useState<boolean>(false);

	return (
		<>
			<MultiValueLozenge
				attributeValueType={attributeValuesInfo.type}
				count={attributeValuesInfo.attributeValues.length}
				onClick={() => {
					setIsEditableAttributeModalOpen(true);
				}}
			/>
			<AsyncEditableAttributeModal
				isOpen={isEditableAttributeModalOpen}
				isEditable={false}
				onClose={() => {
					setIsEditableAttributeModalOpen(false);
				}}
				attributeName={attributeName}
				attributeValuesInfo={attributeValuesInfo}
			/>
		</>
	);
};

export const ScalableReadView = ({ attribute }: ReadViewProps) =>
	matchInsightObjectAttribute(attribute, {
		default: (defaultAttribute) => {
			const selectOptionsValue = getDefaultAttributeArrayValue(attribute);
			return defaultAttribute.objectTypeAttribute.defaultType.name === 'Select' ? (
				<LozengeAndAttributeValueModal
					attributeValuesInfo={{
						type: 'select',
						attributeValues: selectOptionsValue,
					}}
					attributeName={attribute.objectTypeAttribute.name}
				/>
			) : (
				renderDefaultAttribute({
					defaultAttribute,
				})
			);
		},
		object: () => {
			const objectValue = getObjectReferenceOptions(attribute);
			return (
				<LozengeAndAttributeValueModal
					attributeValuesInfo={{ type: 'object', attributeValues: objectValue }}
					attributeName={attribute.objectTypeAttribute.name}
				/>
			);
		},
		user: () => {
			const userValue = getUserValues(attribute);
			return (
				<LozengeAndAttributeValueModal
					attributeValuesInfo={{ type: 'user', attributeValues: userValue }}
					attributeName={attribute.objectTypeAttribute.name}
				/>
			);
		},

		group: () => {
			const groupValue = getGroupValues(attribute);
			return (
				<LozengeAndAttributeValueModal
					attributeValuesInfo={{ type: 'group', attributeValues: groupValue }}
					attributeName={attribute.objectTypeAttribute.name}
				/>
			);
		},

		project: () => {
			const projectValue = getProjectValues(attribute);
			return (
				<LozengeAndAttributeValueModal
					attributeValuesInfo={{ type: 'project', attributeValues: projectValue }}
					attributeName={attribute.objectTypeAttribute.name}
				/>
			);
		},
		bitbucketRepo: () => {
			const bitbucketRepoValue = getBBRepoValues(attribute);
			return (
				<LozengeAndAttributeValueModal
					attributeValuesInfo={{ type: 'bitbucket', attributeValues: bitbucketRepoValue }}
					attributeName={attribute.objectTypeAttribute.name}
				/>
			);
		},
		status: (statusAttribute: StatusAttribute) => {
			const [status = null] = statusAttribute.statusTypeValues.map<StatusReadViewType>(
				(statusAttributeValue) => {
					const { id, name, category } = statusAttributeValue.status;
					return { id, name, category };
				},
			);
			return <StatusReadView status={status} />;
		},
		opsgenieTeam: () => {
			const opsgenieTeamValue = getOpsgenieTeamValues(attribute);
			return (
				<LozengeAndAttributeValueModal
					attributeValuesInfo={{
						type: 'opsgenieTeam',
						attributeValues: opsgenieTeamValue,
					}}
					attributeName={attribute.objectTypeAttribute.name}
				/>
			);
		},
	});
